/**
 * Um diálogo só para criar e para editar o link.
 *
 * O que NÃO tem aqui, e cada ausência é uma regra do contrato:
 *
 *  - **campo de endereço na EDIÇÃO.** O slug se escolhe uma vez, na criação, e nunca mais:
 *    ele já pode estar dentro de um anúncio no ar, e trocá-lo faria esse anúncio apontar
 *    para o nada. Não existe rota para isso no servidor — a tela não inventa uma.
 *  - **campo de URL de destino.** O destino de reserva é escolhido por ID dentro da
 *    allowlist do workspace (invariante 7); URL solta não entra por aqui em hipótese alguma.
 *
 * O que PASSOU a ter: **a escolha da estratégia**. Enquanto o enum tinha um valor só, um
 * select de um item era escolha falsa; hoje são três, e cada uma muda para onde o dinheiro
 * do anúncio vai parar. Por isso nenhuma delas aparece só com o nome: **toda opção vem com a
 * consequência escrita embaixo**, porque escolha sem consequência à vista é chute — e o
 * chute aqui custa um dia de anúncio.
 */
import { useEffect } from 'react';
import { Button, Dialog, Field, Input } from '@mgdlead/ui';

import { FormAlert } from '@mgdlead/ui';
import { NativeSelect } from '@mgdlead/ui';
import { redirectorSchema } from '../../../lib/schemas';
import type { RedirectorValues } from '../../../lib/schemas';
import { useZodForm } from '../../../lib/useZodForm';
import {
  GRUPO_FIXO_QUANDO_ENCHE,
  NO_GROUP_ACTION,
  NO_GROUP_ACTION_ORDER,
  REDIRECTOR_STRATEGY,
  STRATEGY_ORDER,
} from '../../../features/redirector/redirector-ui';
import type { NoGroupAction, RedirectorStrategy } from '../../../features/redirector/redirector-ui';
import { useCampaignGroups, useGroups } from '../../../lib/api';
import type { Campaign, RedirectorAllowlistUrl } from '../../../lib/api';

export interface RedirectorFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  workspaceId: string;
  campaigns: readonly Campaign[];
  allowlist: readonly RedirectorAllowlistUrl[];
  initialName?: string;
  initialCampaignId?: string;
  initialFallbackUrlId?: string;
  initialStrategy?: RedirectorStrategy;
  initialPinnedGroupId?: string;
  initialNoGroupAction?: NoGroupAction;
  initialContingencyGroupId?: string;
  onSubmit: (values: {
    name: string;
    campaignId: string;
    slug: string;
    fallbackUrlId: string;
    strategy: RedirectorStrategy;
    pinnedGroupId: string;
    noGroupAction: NoGroupAction;
    contingencyGroupId: string;
  }) => Promise<void>;
}

const FORM_ID = 'redirector-form';

export function RedirectorFormDialog({
  open,
  onOpenChange,
  mode,
  workspaceId,
  campaigns,
  allowlist,
  initialName = '',
  initialCampaignId = '',
  initialFallbackUrlId = '',
  initialStrategy = 'FILL_SEQUENTIAL',
  initialPinnedGroupId = '',
  initialNoGroupAction = 'FALLBACK_URL',
  initialContingencyGroupId = '',
  onSubmit,
}: RedirectorFormDialogProps) {
  const form = useZodForm({
    schema: redirectorSchema,
    initialValues: {
      name: initialName,
      campaignId: initialCampaignId,
      slug: '',
      fallbackUrlId: initialFallbackUrlId,
      strategy: initialStrategy,
      pinnedGroupId: initialPinnedGroupId,
      noGroupAction: initialNoGroupAction,
      contingencyGroupId: initialContingencyGroupId,
    } satisfies RedirectorValues,
    onSubmit: async (values) => {
      await onSubmit({
        name: values.name,
        campaignId: values.campaignId,
        slug: values.slug,
        fallbackUrlId: values.fallbackUrlId,
        strategy: values.strategy,
        pinnedGroupId: values.pinnedGroupId,
        noGroupAction: values.noGroupAction,
        contingencyGroupId: values.contingencyGroupId,
      });
      onOpenChange(false);
    },
  });

  const { reset } = form;
  // Reabrir com o texto da vez passada editaria o link errado.
  useEffect(() => {
    if (open) {
      reset({
        name: initialName,
        campaignId: initialCampaignId,
        slug: '',
        fallbackUrlId: initialFallbackUrlId,
        strategy: initialStrategy,
        pinnedGroupId: initialPinnedGroupId,
        noGroupAction: initialNoGroupAction,
        contingencyGroupId: initialContingencyGroupId,
      });
    }
  }, [
    open,
    initialName,
    initialCampaignId,
    initialFallbackUrlId,
    initialStrategy,
    initialPinnedGroupId,
    initialNoGroupAction,
    initialContingencyGroupId,
    reset,
  ]);

  const semCampanha = campaigns.length === 0;
  const grupoFixo = form.values.strategy === 'SPECIFIC_GROUP';

  /**
   * **Os grupos da CAMPANHA ESCOLHIDA NO FORMULÁRIO**, e não os do workspace.
   *
   * O servidor recusa fixar um grupo que não esteja vinculado à campanha do link
   * (`assertStrategyCoherent`), e com razão: um link apontando para grupo de fora seria uma
   * terceira ordem de distribuição, invisível na gaveta de grupos da campanha. Oferecer aqui
   * a lista inteira do workspace seria oferecer uma escolha que o servidor recusa — e o erro
   * só apareceria depois de a pessoa preencher o resto.
   *
   * Depende de `form.values.campaignId` (não do `initialCampaignId`): trocar a campanha no
   * meio do preenchimento tem de trocar a lista junto.
   */
  const grupos = useCampaignGroups(
    workspaceId,
    grupoFixo && form.values.campaignId.length > 0 ? form.values.campaignId : null,
  );
  /* Grupo fora da operação não se fixa: ele não recebe ninguém. */
  const grupoOpcoes = (grupos.data ?? []).filter((grupo) => grupo.archivedAt === null);

  /**
   * B6 — o grupo de contingência é QUALQUER grupo vivo do workspace (não precisa estar na
   * campanha: é o que recebe quando a campanha não tem para onde mandar). A consulta só sai
   * quando a opção está marcada.
   */
  const contingencia = form.values.noGroupAction === 'CONTINGENCY_GROUP';
  const gruposDoWorkspace = useGroups(workspaceId);
  const contingenciaOpcoes = contingencia ? (gruposDoWorkspace.data?.groups ?? []) : [];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === 'create' ? 'Novo redirecionador' : 'Editar redirecionador'}
      description={
        mode === 'create'
          ? 'O link curto que você põe no botão da sua página. Você escolhe abaixo como ele reparte os cliques entre os grupos da campanha.'
          : undefined
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={form.pending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={form.pending}
            loadingLabel="Salvando"
            disabled={semCampanha}
            form={FORM_ID}
            type="submit"
          >
            {mode === 'create' ? 'Criar link' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-3">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        {/*
          Redirecionador sem campanha não existe no contrato (`campaignId` é obrigatório na
          criação). Dizer isso ANTES é melhor do que um select vazio que só revela o problema
          depois que a pessoa preencheu o resto.
        */}
        {semCampanha ? (
          <FormAlert tone="warning">
            Todo link pertence a uma campanha, e você ainda não tem nenhuma. Crie uma campanha
            primeiro — é ela que diz para quais grupos este link pode mandar gente.
          </FormAlert>
        ) : null}

        <Field
          label="Nome do link"
          hint="Para você reconhecer na lista: “Botão da LP de setembro”, “Story do Instagram”."
          error={form.errors.name}
          required
        >
          {(control) => (
            <Input
              {...control}
              name="name"
              autoComplete="off"
              placeholder="Botão da landing page"
              value={form.values.name}
              invalid={form.errors.name !== undefined}
              onChange={(event) => form.setField('name', event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Campanha"
          hint="Os grupos que podem receber estes cliques são os da campanha. Dá para repontar o link depois — o histórico de cada clique continua com a campanha da época."
          error={form.errors.campaignId}
          required
        >
          {(control) => (
            <NativeSelect
              {...control}
              name="campaignId"
              value={form.values.campaignId}
              invalid={form.errors.campaignId !== undefined}
              disabled={semCampanha}
              onChange={(event) => {
                form.setField('campaignId', event.target.value);
                /*
                 * Trocar a campanha invalida o grupo fixo: ele pertencia à campanha antiga, e
                 * o servidor recusaria a combinação. Limpar aqui evita um erro que a tela
                 * podia ter evitado — e o campo volta a pedir a escolha, visivelmente.
                 */
                if (form.values.pinnedGroupId.length > 0) form.setField('pinnedGroupId', '');
              }}
            >
              <option value="">Escolha a campanha…</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name} ({campaign.code})
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>

        {/* ------------------------- como reparte os cliques ------------------------- */}
        <EscolhaDeEstrategia
          value={form.values.strategy}
          onChange={(valor) => {
            form.setField('strategy', valor);
            // Saiu do grupo fixo: o grupo fixado deixa de fazer sentido (e o servidor o
            // limpa sozinho). Manter o valor aqui faria a tela prometer o que não vale.
            if (valor !== 'SPECIFIC_GROUP') form.setField('pinnedGroupId', '');
          }}
        />

        {grupoFixo ? (
          <Field
            label="Grupo fixo"
            hint="Só aparecem aqui os grupos vinculados à campanha escolhida acima — é o que o servidor aceita."
            error={form.errors.pinnedGroupId}
            required
          >
            {(control) => (
              <NativeSelect
                {...control}
                name="pinnedGroupId"
                value={form.values.pinnedGroupId}
                invalid={form.errors.pinnedGroupId !== undefined}
                disabled={form.values.campaignId.length === 0}
                onChange={(event) => form.setField('pinnedGroupId', event.target.value)}
              >
                <option value="">
                  {form.values.campaignId.length === 0
                    ? 'Escolha a campanha primeiro…'
                    : 'Escolha o grupo…'}
                </option>
                {grupoOpcoes.map((grupo) => (
                  <option key={grupo.groupId} value={grupo.groupId}>
                    {grupo.name ?? 'Sem nome no WhatsApp'}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        ) : null}

        {/*
          O aviso vem ANTES de salvar, e não num parágrafo de ajuda depois: "não desvia" é
          deliberado, e quem descobre no dia em que o grupo encheu lê isso como bug.
        */}
        {grupoFixo ? <FormAlert tone="warning">{GRUPO_FIXO_QUANDO_ENCHE}</FormAlert> : null}

        {grupoFixo && form.values.campaignId.length > 0 && !grupos.isPending ? (
          grupoOpcoes.length === 0 ? (
            <FormAlert tone="warning">
              Esta campanha não tem nenhum grupo monitorado vinculado, então não há grupo para
              fixar. Vincule um grupo à campanha antes de usar esta estratégia.
            </FormAlert>
          ) : null
        ) : null}

        {/*
          Só na criação. Depois de criado, o endereço é imutável — e não por rigidez: ele já
          pode estar colado num anúncio pago.
        */}
        {mode === 'create' ? (
          <Field
            label="Endereço (opcional)"
            hint="O pedaço que vem depois da barra. Deixe em branco e nós sorteamos um. Depois de criado, o endereço não muda mais."
            error={form.errors.slug}
          >
            {(control) => (
              <Input
                {...control}
                name="slug"
                autoComplete="off"
                placeholder="entrar-no-vip"
                value={form.values.slug}
                invalid={form.errors.slug !== undefined}
                onChange={(event) => form.setField('slug', event.target.value)}
              />
            )}
          </Field>
        ) : null}

        <EscolhaSemGrupo
          value={form.values.noGroupAction}
          onChange={(valor) => {
            form.setField('noGroupAction', valor);
            // Saiu da contingência: o grupo deixa de valer (o servidor também o limpa).
            if (valor !== 'CONTINGENCY_GROUP') form.setField('contingencyGroupId', '');
          }}
        />

        {contingencia ? (
          <Field
            label="Grupo de contingência"
            hint="Qualquer grupo seu, mesmo fora desta campanha. Ele passa pela mesma conferência de lotação e convite dos outros."
            error={form.errors.contingencyGroupId}
            required
          >
            {(control) => (
              <NativeSelect
                {...control}
                name="contingencyGroupId"
                value={form.values.contingencyGroupId}
                invalid={form.errors.contingencyGroupId !== undefined}
                disabled={gruposDoWorkspace.isPending}
                onChange={(event) => form.setField('contingencyGroupId', event.target.value)}
              >
                <option value="">
                  {gruposDoWorkspace.isPending ? 'Carregando os grupos…' : 'Escolha o grupo…'}
                </option>
                {contingenciaOpcoes.map((grupo) => (
                  <option key={grupo.id} value={grupo.id}>
                    {grupo.name ?? 'Sem nome no WhatsApp'}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        ) : null}

        <Field
          label="Destino de reserva"
          hint={
            allowlist.length === 0
              ? 'Você ainda não cadastrou nenhum destino autorizado. Sem reserva, quem clicar sem grupo disponível vê uma página de indisponível.'
              : 'Para onde a pessoa vai quando nenhum grupo da campanha tem vaga. Só destinos já autorizados aparecem aqui.'
          }
          error={form.errors.fallbackUrlId}
        >
          {(control) => (
            <NativeSelect
              {...control}
              name="fallbackUrlId"
              value={form.values.fallbackUrlId}
              disabled={allowlist.length === 0}
              onChange={(event) => form.setField('fallbackUrlId', event.target.value)}
            >
              <option value="">Sem destino de reserva</option>
              {allowlist.map((url) => (
                <option key={url.id} value={url.id}>
                  {url.label}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </form>
    </Dialog>
  );
}

/* ========================================================================== */
/*  A escolha da estratégia                                                    */
/* ========================================================================== */

/**
 * Rádio, e não `<select>`.
 *
 * Um `<select>` mostra três nomes e esconde o que cada um faz — e num `<select>` nativo não
 * cabe a consequência embaixo da opção. Aqui as três ficam abertas ao mesmo tempo, cada uma
 * com **o que acontece** e **o que custa**, que é o único jeito de a escolha ser uma escolha
 * e não um chute. O estado marcado se reconhece pela FORMA (o próprio rádio e a borda), não
 * só pela cor.
 */
function EscolhaDeEstrategia({
  value,
  onChange,
}: {
  value: RedirectorStrategy;
  onChange: (value: RedirectorStrategy) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium">Como este link reparte os cliques</legend>
      {STRATEGY_ORDER.map((chave) => {
        const spec = REDIRECTOR_STRATEGY[chave];
        const marcado = value === chave;
        return (
          <label
            key={chave}
            className={
              'flex cursor-pointer gap-2 rounded-md border p-3 text-left ' +
              (marcado ? 'border-primary bg-muted/40' : 'border-border hover:bg-muted/30')
            }
          >
            <input
              type="radio"
              name="strategy"
              value={chave}
              checked={marcado}
              onChange={() => onChange(chave)}
              className="mt-0.5 size-4 shrink-0 accent-primary"
            />
            <span className="flex min-w-0 flex-col gap-1">
              <span className="text-sm font-medium">{spec.label}</span>
              {/* A CONSEQUÊNCIA, não o nome: é o que transforma a lista numa decisão. */}
              <span className="text-xs text-muted-foreground">{spec.consequence}</span>
              <span className="text-2xs text-muted-foreground">{spec.tradeoff}</span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

/* ========================================================================== */
/*  B6 — o que fazer quando nenhum grupo pode receber                          */
/* ========================================================================== */

/** Rádio com a consequência embaixo, no mesmo desenho da escolha de estratégia. */
function EscolhaSemGrupo({
  value,
  onChange,
}: {
  value: NoGroupAction;
  onChange: (value: NoGroupAction) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium">
        Quando nenhum grupo puder receber (convite quebrado ou lotação)
      </legend>
      {NO_GROUP_ACTION_ORDER.map((chave) => {
        const spec = NO_GROUP_ACTION[chave];
        const marcado = value === chave;
        return (
          <label
            key={chave}
            className={
              'flex cursor-pointer gap-2 rounded-md border p-3 text-left ' +
              (marcado ? 'border-primary bg-muted/40' : 'border-border hover:bg-muted/30')
            }
          >
            <input
              type="radio"
              name="noGroupAction"
              value={chave}
              checked={marcado}
              onChange={() => onChange(chave)}
              className="mt-0.5 size-4 shrink-0 accent-primary"
            />
            <span className="flex min-w-0 flex-col gap-1">
              <span className="text-sm font-medium">{spec.label}</span>
              <span className="text-xs text-muted-foreground">{spec.consequence}</span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
